/**
 * Cancellation reason types for frontend
 * Matches backend enums from backend-v2/src/common/enums/cancellation-reason.enum.ts
 */

/**
 * Standardized cancellation reasons for ORDER-ONLY cancellations
 * Used when cancelling just the order (subscription remains active)
 */
export enum CancellationReason {
  /** Patient's treatment is temporarily on hold */
  PATIENT_TREATMENT_ON_HOLD = 'patient_treatment_on_hold',

  /** Patient has more than a month of medication on hand */
  PATIENT_HAS_MEDICATION_ON_HAND = 'patient_has_medication_on_hand',

  /** Billing or payment issue */
  BILLING_ISSUE = 'billing_issue',

  /** Missed delivery or pickup, needs rescheduling */
  MISSED_DELIVERY_PICKUP = 'missed_delivery_pickup',

  /** Patient not responsive during call */
  PATIENT_NOT_RESPONSIVE = 'patient_not_responsive',
}

/**
 * Human-readable labels for order-only cancellation reasons
 */
export const CANCELLATION_REASON_LABELS: Record<CancellationReason, string> = {
  [CancellationReason.PATIENT_TREATMENT_ON_HOLD]: 'Patient Treatment on Hold',
  [CancellationReason.PATIENT_HAS_MEDICATION_ON_HAND]:
    'Patient has more than a Month of medication on hand',
  [CancellationReason.BILLING_ISSUE]: 'Billing Issue',
  [CancellationReason.MISSED_DELIVERY_PICKUP]: 'Missed Delivery/Pickup - Please Reschedule',
  [CancellationReason.PATIENT_NOT_RESPONSIVE]: 'Patient not responsive (on call)',
};

/**
 * Get order-only cancellation reasons in display order
 */
export function getCancellationReasons(): CancellationReason[] {
  return [
    CancellationReason.PATIENT_TREATMENT_ON_HOLD,
    CancellationReason.PATIENT_HAS_MEDICATION_ON_HAND,
    CancellationReason.BILLING_ISSUE,
    CancellationReason.MISSED_DELIVERY_PICKUP,
    CancellationReason.PATIENT_NOT_RESPONSIVE,
  ];
}

/**
 * Get label for an order-only cancellation reason
 */
export function getCancellationReasonLabel(reason: CancellationReason): string {
  return CANCELLATION_REASON_LABELS[reason] || 'Unknown Reason';
}

/**
 * Standardized cancellation reasons for SUBSCRIPTION cancellations
 * Used when cancelling subscriptions (with or without associated order)
 */
export enum SubscriptionCancellationReason {
  /** Patient has passed away */
  PATIENT_DECEASED = 'patient_deceased',

  /** PPN - Patient preference not to continue / free-of-charge program */
  PPN = 'ppn',

  /** Patient has stopped responding to outreach (GP-857) */
  PATIENT_UNRESPONSIVE = 'patient_unresponsive',

  /** Subscription was created/enrolled in error (GP-1143) */
  IN_ERROR = 'in_error',

  /** Patient prefers on-demand orders over a recurring subscription (GP-1143) */
  PATIENT_WANTS_ON_DEMAND_ONLY = 'patient_wants_on_demand_only',

  /** Medication discontinued by prescriber */
  MEDICATION_DISCONTINUED_BY_PRESCRIBER = 'medication_discontinued_by_prescriber',

  /** Medication switched by prescriber */
  MEDICATION_SWITCHED_BY_PRESCRIBER = 'medication_switched_by_prescriber',

  /** Patient transferred to external pharmacy due to delivery service complaint */
  TRANSFERRED_OUT_SERVICE_COMPLAINT_DELIVERY = 'transferred_out_service_complaint_delivery',

  /** Patient transferred to another GreenShield pharmacy */
  TRANSFERRED_TO_ANOTHER_GREENSHIELD = 'transferred_to_another_greenshield',

  /** Patient transferred to external pharmacy by patient preference */
  TRANSFERRED_OUT_PATIENT_PREFERENCE = 'transferred_out_patient_preference',

  /** Patient transferred to external pharmacy due to dispensing service complaint */
  TRANSFERRED_OUT_SERVICE_COMPLAINT_DISPENSING = 'transferred_out_service_complaint_dispensing',

  /** Patient switching from IV clinic to home delivery */
  SWITCHING_TO_HOME_DELIVERY_FROM_IV = 'switching_to_home_delivery_from_iv',
}

/**
 * Human-readable labels for subscription cancellation reasons
 */
export const SUBSCRIPTION_CANCELLATION_REASON_LABELS: Record<SubscriptionCancellationReason, string> = {
  [SubscriptionCancellationReason.PATIENT_DECEASED]: 'Patient Deceased',
  [SubscriptionCancellationReason.PPN]: 'PPN/FOC',
  [SubscriptionCancellationReason.PATIENT_UNRESPONSIVE]: 'Patient Unresponsive',
  [SubscriptionCancellationReason.IN_ERROR]: 'In Error',
  [SubscriptionCancellationReason.PATIENT_WANTS_ON_DEMAND_ONLY]: 'Patient Wants On-Demand Only',
  [SubscriptionCancellationReason.MEDICATION_DISCONTINUED_BY_PRESCRIBER]:
    'Medication Discontinued by Prescriber',
  [SubscriptionCancellationReason.MEDICATION_SWITCHED_BY_PRESCRIBER]:
    'Medication Switched by Prescriber',
  [SubscriptionCancellationReason.TRANSFERRED_OUT_SERVICE_COMPLAINT_DELIVERY]:
    'Patient Transferred Out to External Pharmacy - Service Complaint - Delivery',
  [SubscriptionCancellationReason.TRANSFERRED_TO_ANOTHER_GREENSHIELD]:
    'Patient Transferred to Another GreenShield Pharmacy',
  [SubscriptionCancellationReason.TRANSFERRED_OUT_PATIENT_PREFERENCE]:
    'Patient Transferred Out to External Pharmacy - Pt. Preference',
  [SubscriptionCancellationReason.TRANSFERRED_OUT_SERVICE_COMPLAINT_DISPENSING]:
    'Patient Transferred Out to External Pharmacy - Service Complaint - Dispensing',
  [SubscriptionCancellationReason.SWITCHING_TO_HOME_DELIVERY_FROM_IV]:
    'Patient Switching to Home Delivery from IV',
};

/**
 * Get subscription cancellation reasons in display order
 */
export function getSubscriptionCancellationReasons(): SubscriptionCancellationReason[] {
  // GP-857: Patient Deceased deliberately LAST (was first — too easy to
  // mis-click for the most sensitive reason); Patient Unresponsive added.
  return [
    SubscriptionCancellationReason.PPN,
    SubscriptionCancellationReason.PATIENT_UNRESPONSIVE,
    SubscriptionCancellationReason.IN_ERROR,
    SubscriptionCancellationReason.PATIENT_WANTS_ON_DEMAND_ONLY,
    SubscriptionCancellationReason.MEDICATION_DISCONTINUED_BY_PRESCRIBER,
    SubscriptionCancellationReason.MEDICATION_SWITCHED_BY_PRESCRIBER,
    SubscriptionCancellationReason.TRANSFERRED_OUT_SERVICE_COMPLAINT_DELIVERY,
    SubscriptionCancellationReason.TRANSFERRED_TO_ANOTHER_GREENSHIELD,
    SubscriptionCancellationReason.TRANSFERRED_OUT_PATIENT_PREFERENCE,
    SubscriptionCancellationReason.TRANSFERRED_OUT_SERVICE_COMPLAINT_DISPENSING,
    SubscriptionCancellationReason.SWITCHING_TO_HOME_DELIVERY_FROM_IV,
    SubscriptionCancellationReason.PATIENT_DECEASED,
  ];
}

/**
 * Get label for a subscription cancellation reason
 */
export function getSubscriptionCancellationReasonLabel(reason: SubscriptionCancellationReason): string {
  return SUBSCRIPTION_CANCELLATION_REASON_LABELS[reason] || 'Unknown Reason';
}

/**
 * Reasons for removing an individual medication from a subscription
 * (subscription remains active with remaining medications)
 */
export enum MedicationRemovalReason {
  MEDICATION_DISCONTINUED_BY_PRESCRIBER = 'medication_discontinued_by_prescriber',
  MEDICATION_SWITCHED_BY_PRESCRIBER = 'medication_switched_by_prescriber',
  PATIENT_PREFERENCE = 'patient_preference',
  ADVERSE_REACTION = 'adverse_reaction',
  NO_LONGER_NEEDED = 'no_longer_needed',
  DUPLICATE_PRESCRIPTION = 'duplicate_prescription',
  OTHER = 'other',
}

export const MEDICATION_REMOVAL_REASON_LABELS: Record<MedicationRemovalReason, string> = {
  [MedicationRemovalReason.MEDICATION_DISCONTINUED_BY_PRESCRIBER]: 'Medication Discontinued by Prescriber',
  [MedicationRemovalReason.MEDICATION_SWITCHED_BY_PRESCRIBER]: 'Medication Switched by Prescriber',
  [MedicationRemovalReason.PATIENT_PREFERENCE]: 'Patient Preference',
  [MedicationRemovalReason.ADVERSE_REACTION]: 'Adverse Reaction',
  [MedicationRemovalReason.NO_LONGER_NEEDED]: 'No Longer Needed',
  [MedicationRemovalReason.DUPLICATE_PRESCRIPTION]: 'Duplicate Prescription',
  [MedicationRemovalReason.OTHER]: 'Other',
};

export function getMedicationRemovalReasons(): MedicationRemovalReason[] {
  return [
    MedicationRemovalReason.MEDICATION_DISCONTINUED_BY_PRESCRIBER,
    MedicationRemovalReason.MEDICATION_SWITCHED_BY_PRESCRIBER,
    MedicationRemovalReason.PATIENT_PREFERENCE,
    MedicationRemovalReason.ADVERSE_REACTION,
    MedicationRemovalReason.NO_LONGER_NEEDED,
    MedicationRemovalReason.DUPLICATE_PRESCRIPTION,
    MedicationRemovalReason.OTHER,
  ];
}

export function getMedicationRemovalReasonLabel(reason: MedicationRemovalReason): string {
  return MEDICATION_REMOVAL_REASON_LABELS[reason] || 'Unknown Reason';
}
